import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Code,
  Plus,
  Loader2,
  Trash2,
  Pencil,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronDown,
  FileText,
  Timer,
  RotateCcw,
} from "lucide-react";

interface Script {
  id: string;
  name: string;
  code: string;
  schedule: string | null;
  target_url: string | null;
  selectors: any;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface ScriptReport {
  id: string;
  script_id: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
  file_name: string | null;
  file_path: string | null;
  file_size: number | null;
  row_count: number | null;
  logs: string | null;
  retry_count: number;
  sync_status: string;
  created_at: string;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ElementType }> = {
  pending: { label: "Pending", variant: "secondary", icon: Clock },
  running: { label: "Running", variant: "outline", icon: Loader2 },
  success: { label: "Success", variant: "default", icon: CheckCircle2 },
  failed: { label: "Failed", variant: "destructive", icon: XCircle },
};

const emptyScript = { name: "", code: "", schedule: "", target_url: "", selectors: "{}" };

const ScriptsManager = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [scripts, setScripts] = useState<Script[]>([]);
  const [reports, setReports] = useState<Record<string, ScriptReport[]>>({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScript, setEditingScript] = useState<Script | null>(null);
  const [form, setForm] = useState(emptyScript);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [expandedScript, setExpandedScript] = useState<string | null>(null);
  const [loadingReports, setLoadingReports] = useState<string | null>(null);

  const fetchScripts = async () => {
    const { data, error } = await supabase
      .from("scripts" as any)
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setScripts((data as unknown as Script[]) || []);
    }
    setLoading(false);
  };

  const fetchReports = async (scriptId: string) => {
    setLoadingReports(scriptId);
    const { data, error } = await supabase
      .from("script_reports" as any)
      .select("*")
      .eq("script_id", scriptId)
      .order("created_at", { ascending: false })
      .limit(20);
    if (!error && data) {
      setReports((prev) => ({ ...prev, [scriptId]: data as unknown as ScriptReport[] }));
    }
    setLoadingReports(null);
  };

  useEffect(() => {
    fetchScripts();
  }, []);

  const openCreate = () => {
    setEditingScript(null);
    setForm(emptyScript);
    setDialogOpen(true);
  };

  const openEdit = (script: Script) => {
    setEditingScript(script);
    setForm({
      name: script.name,
      code: script.code,
      schedule: script.schedule || "",
      target_url: script.target_url || "",
      selectors: typeof script.selectors === "string" ? script.selectors : JSON.stringify(script.selectors, null, 2),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: "Validation", description: "Name is required.", variant: "destructive" });
      return;
    }

    let parsedSelectors = {};
    try {
      parsedSelectors = form.selectors.trim() ? JSON.parse(form.selectors) : {};
    } catch {
      toast({ title: "Validation", description: "Selectors must be valid JSON.", variant: "destructive" });
      return;
    }

    setSaving(true);
    const payload = {
      name: form.name.trim(),
      code: form.code,
      schedule: form.schedule.trim() || null,
      target_url: form.target_url.trim() || null,
      selectors: parsedSelectors,
    };

    if (editingScript) {
      const { error } = await supabase
        .from("scripts" as any)
        .update(payload)
        .eq("id", editingScript.id);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Updated", description: `Script "${form.name}" updated.` });
        setDialogOpen(false);
        fetchScripts();
      }
    } else {
      const { error } = await supabase
        .from("scripts" as any)
        .insert({ ...payload, client_id: profile?.client_id } as any);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Created", description: `Script "${form.name}" created.` });
        setDialogOpen(false);
        fetchScripts();
      }
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from("scripts" as any).delete().eq("id", deleteId);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: "Script removed." });
      fetchScripts();
    }
    setDeleteId(null);
  };

  const handleToggle = async (script: Script) => {
    const { error } = await supabase
      .from("scripts" as any)
      .update({ enabled: !script.enabled })
      .eq("id", script.id);
    if (!error) {
      setScripts((prev) => prev.map((s) => (s.id === script.id ? { ...s, enabled: !s.enabled } : s)));
    }
  };

  const toggleExpand = (scriptId: string) => {
    if (expandedScript === scriptId) {
      setExpandedScript(null);
    } else {
      setExpandedScript(scriptId);
      if (!reports[scriptId]) {
        fetchReports(scriptId);
      }
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return "—";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatSize = (bytes: number | null) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Scripts</h1>
          <p className="text-sm text-muted-foreground">
            Manage Playwright scripts that run on a headless browser to download files.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" />
          Add Script
        </Button>
      </div>

      {scripts.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <Code className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-lg mb-1">No scripts yet</CardTitle>
          <CardDescription>Add a Playwright script to start automating file downloads.</CardDescription>
        </Card>
      ) : (
        <div className="space-y-3">
          {scripts.map((script) => (
            <Card key={script.id}>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <Switch
                      checked={script.enabled}
                      onCheckedChange={() => handleToggle(script)}
                    />
                    <div className="min-w-0">
                      <CardTitle className="text-sm font-medium truncate">{script.name}</CardTitle>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        {script.schedule && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {script.schedule}
                          </span>
                        )}
                        {script.target_url && (
                          <span className="truncate max-w-[200px]">{script.target_url}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(script)} title="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => toggleExpand(script.id)} title="View Reports">
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteId(script.id)} title="Delete">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              {expandedScript === script.id && (
                <CardContent className="pt-0 px-4 pb-4">
                  <div className="border-t pt-3">
                    <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                      Execution Reports
                    </h4>
                    {loadingReports === script.id ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : !reports[script.id] || reports[script.id].length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2">No executions yet.</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs">Started</TableHead>
                            <TableHead className="text-xs">Duration</TableHead>
                            <TableHead className="text-xs">File</TableHead>
                            <TableHead className="text-xs">Rows</TableHead>
                            <TableHead className="text-xs">Retries</TableHead>
                            <TableHead className="text-xs">Sync</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reports[script.id].map((report) => {
                            const st = statusConfig[report.status] || statusConfig.pending;
                            const StIcon = st.icon;
                            return (
                              <TableRow key={report.id}>
                                <TableCell>
                                  <Badge variant={st.variant} className="gap-1 text-xs">
                                    <StIcon className={`h-3 w-3 ${report.status === "running" ? "animate-spin" : ""}`} />
                                    {st.label}
                                  </Badge>
                                  {report.error_message && (
                                    <div className="text-xs text-destructive mt-1 max-w-[200px] truncate" title={report.error_message}>
                                      {report.error_message}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  {report.started_at
                                    ? new Date(report.started_at).toLocaleString()
                                    : "—"}
                                </TableCell>
                                <TableCell className="text-xs">
                                  <span className="flex items-center gap-1">
                                    <Timer className="h-3 w-3" />
                                    {formatDuration(report.duration_ms)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-xs">
                                  {report.file_name ? (
                                    <span className="truncate max-w-[120px] block" title={report.file_name}>
                                      {report.file_name}
                                    </span>
                                  ) : "—"}
                                </TableCell>
                                <TableCell className="text-xs">{report.row_count ?? "—"}</TableCell>
                                <TableCell className="text-xs">
                                  {report.retry_count > 0 && (
                                    <span className="flex items-center gap-1">
                                      <RotateCcw className="h-3 w-3" />
                                      {report.retry_count}
                                    </span>
                                  )}
                                  {report.retry_count === 0 && "—"}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const syncSt = statusConfig[report.sync_status] || statusConfig.pending;
                                    return (
                                      <Badge variant={syncSt.variant} className="text-xs">
                                        {syncSt.label}
                                      </Badge>
                                    );
                                  })()}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                  {reports[script.id]?.some((r) => r.logs) && (
                    <div className="mt-3 border-t pt-3">
                      <Collapsible>
                        <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground">
                          <ChevronDown className="h-3 w-3" />
                          Latest Logs
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <pre className="mt-2 max-h-40 overflow-auto rounded bg-muted p-2 text-xs font-mono">
                            {reports[script.id].find((r) => r.logs)?.logs}
                          </pre>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingScript ? "Edit Script" : "Add Script"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Download daily report"
              />
            </div>
            <div className="space-y-2">
              <Label>Target URL</Label>
              <Input
                value={form.target_url}
                onChange={(e) => setForm((f) => ({ ...f, target_url: e.target.value }))}
                placeholder="https://example.com/reports"
              />
            </div>
            <div className="space-y-2">
              <Label>Schedule (cron expression)</Label>
              <Input
                value={form.schedule}
                onChange={(e) => setForm((f) => ({ ...f, schedule: e.target.value }))}
                placeholder="0 9 * * * (daily at 9am)"
              />
            </div>
            <div className="space-y-2">
              <Label>Selectors (JSON)</Label>
              <Textarea
                value={form.selectors}
                onChange={(e) => setForm((f) => ({ ...f, selectors: e.target.value }))}
                placeholder='{"loginButton": "#login", "downloadLink": ".report-download"}'
                rows={3}
                className="font-mono text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label>Playwright Code</Label>
              <Textarea
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                placeholder={`// Playwright script\nconst page = await browser.newPage();\nawait page.goto(targetUrl);\n// ...`}
                rows={10}
                className="font-mono text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingScript ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete script?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the script and all its execution reports.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ScriptsManager;
